import type { Severity } from '../types';

export const PROBLEM_PRIORITY: Record<Severity, number> = {
    critical: 4,
    high: 3,
    medium: 2,
    low: 1,
};

// Brand-red themed severity palette — intensity varies by severity level
// Primary brand: #D31145 / #A4001D
export const SEVERITY_COLORS = {
    critical: {
        header: 'from-red-600/20 to-rose-600/20',
        headerStyle: { background: 'linear-gradient(135deg, rgba(211,17,69,0.15), rgba(164,0,29,0.10))' },
        diagnosis: 'from-red-950/30 via-red-900/20 to-transparent',
        prescription: 'from-emerald-950/30 via-emerald-900/20 to-transparent',
        accent: 'red-500',
        badge: 'bg-red-500/20 text-red-300 border-red-500/30',
        badgeStyle: { backgroundColor: 'rgba(211,17,69,0.12)', color: '#A4001D', borderColor: 'rgba(211,17,69,0.30)' },
        indicator: 'bg-gradient-to-r from-red-500 via-rose-500 to-orange-500',
        indicatorStyle: { background: 'linear-gradient(90deg, #D31145, #A4001D, #D31145)' },
        glow: 'shadow-red-500/20'
    },
    high: {
        header: 'from-orange-600/20 to-amber-600/20',
        headerStyle: { background: 'linear-gradient(135deg, rgba(211,17,69,0.10), rgba(234,88,12,0.08))' },
        diagnosis: 'from-orange-950/30 via-orange-900/20 to-transparent',
        prescription: 'from-blue-950/30 via-blue-900/20 to-transparent',
        accent: 'orange-500',
        badge: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
        badgeStyle: { backgroundColor: 'rgba(211,17,69,0.08)', color: '#c2410c', borderColor: 'rgba(211,17,69,0.20)' },
        indicator: 'bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500',
        indicatorStyle: { background: 'linear-gradient(90deg, #D31145, #ea580c, #f59e0b)' },
        glow: 'shadow-orange-500/20'
    },
    medium: {
        header: 'from-yellow-600/20 to-amber-600/20',
        headerStyle: { background: 'linear-gradient(135deg, rgba(211,17,69,0.06), rgba(245,158,11,0.06))' },
        diagnosis: 'from-yellow-950/30 via-yellow-900/20 to-transparent',
        prescription: 'from-violet-950/30 via-violet-900/20 to-transparent',
        accent: 'yellow-500',
        badge: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
        badgeStyle: { backgroundColor: 'rgba(245,158,11,0.10)', color: '#92400e', borderColor: 'rgba(245,158,11,0.25)' },
        indicator: 'bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-400',
        indicatorStyle: { background: 'linear-gradient(90deg, #D31145, #f59e0b, #fbbf24)' },
        glow: 'shadow-yellow-500/20'
    },
    low: {
        header: 'from-slate-600/20 to-gray-600/20',
        headerStyle: { background: 'linear-gradient(135deg, rgba(211,17,69,0.04), rgba(100,116,139,0.06))' },
        diagnosis: 'from-slate-950/30 via-slate-900/20 to-transparent',
        prescription: 'from-cyan-950/30 via-cyan-900/20 to-transparent',
        accent: 'slate-400',
        badge: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
        badgeStyle: { backgroundColor: 'rgba(100,116,139,0.08)', color: '#475569', borderColor: 'rgba(100,116,139,0.20)' },
        indicator: 'bg-gradient-to-r from-slate-400 via-gray-400 to-zinc-400',
        indicatorStyle: { background: 'linear-gradient(90deg, #D31145, #94a3b8, #cbd5e1)' },
        glow: 'shadow-slate-400/20'
    }
} as const;

export type SeverityColorKey = keyof typeof SEVERITY_COLORS;